// La FICHE d'une personne de l'annuaire : ajouter, modifier, effacer.
import { useState, type FormEvent } from 'react';
import { usePersonas } from '../api/queries';
import type { Seniority } from '../api/types';
import { Button, ErrorBanner, Field, Select } from '../components/ui';
import { SENIORITIES } from '../personas/PersonaNeeds';
import { personaTitle } from '../personas/labels';
import { FormShell, TextArea } from '../write/Forms';
import { ApiRequestError } from '../api/client';
import {
  ENGAGEMENTS, personName, useCreatePerson, useErasePerson, useUpdatePerson,
  type Engagement, type Person,
} from './api';

const orNull = (v: string) => (v.trim() ? v.trim() : null);

export function PersonForm({
  projectId,
  existing,
  pools,
  roles,
  onDone,
  onErased,
}: {
  projectId: string;
  existing?: Person;
  pools: string[];
  roles: string[];
  onDone: () => void;
  /** Après l'effacement (à défaut : `onDone`). */
  onErased?: () => void;
}) {
  const [firstName, setFirstName] = useState(existing?.first_name ?? '');
  const [lastName, setLastName] = useState(existing?.last_name ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [jobTitle, setJobTitle] = useState(existing?.job_title ?? '');
  const [role, setRole] = useState(existing?.role ?? '');
  const [seniority, setSeniority] = useState<Seniority | ''>(existing?.seniority ?? '');
  const [engagement, setEngagement] = useState<Engagement | ''>(existing?.engagement ?? '');
  const [pool, setPool] = useState(existing?.pool ?? '');
  const [keyUser, setKeyUser] = useState(existing?.key_user ?? false);
  const [domainExpert, setDomainExpert] = useState(existing?.domain_expert ?? false);
  const [personaId, setPersonaId] = useState(existing?.persona_id ?? '');
  const [doNotContact, setDoNotContact] = useState(existing?.do_not_contact ?? false);
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const personas = usePersonas(projectId);
  const create = useCreatePerson(projectId);
  const update = useUpdatePerson(projectId);

  const ready = firstName.trim() !== '' || lastName.trim() !== '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    const input = {
      firstName: orNull(firstName), lastName: orNull(lastName), email: orNull(email), phone: orNull(phone),
      jobTitle: orNull(jobTitle), role: orNull(role), seniority: seniority || null, engagement: engagement || null,
      pool: orNull(pool), keyUser, domainExpert, personaId: personaId || null, doNotContact, notes: orNull(notes),
    };
    try {
      if (existing) await update.mutateAsync({ id: existing.id, patch: input });
      else await create.mutateAsync(input);
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <>
      <FormShell
        onSubmit={submit}
        submitting={create.isPending || update.isPending}
        error={create.error ?? update.error}
        submitLabel={existing ? 'Enregistrer' : 'Ajouter à l’annuaire'}
        disabled={!ready}
      >
        <div className="field-row">
          <Field label="Prénom" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" />
          <Field label="Nom" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
        </div>
        <div className="field-row">
          <Field
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            hint="Sert à l’inviter, et à repérer les doublons."
          />
          <Field label="Téléphone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
        </div>
        <div className="field-row">
          <Field label="Fonction" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Responsable logistique" />
          <Field
            label="Rôle dans le produit"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Administrateur, utilisateur final…"
            list="ppl-roles"
          />
          <datalist id="ppl-roles">{roles.map((r) => <option key={r} value={r} />)}</datalist>
        </div>
        <div className="field-row">
          <label className="zy-field">
            <span className="zy-field__label">Séniorité</span>
            <Select value={seniority} onChange={(e) => setSeniority(e.target.value as Seniority | '')}>
              <option value="">Non renseignée</option>
              {SENIORITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </label>
          <label className="zy-field">
            <span className="zy-field__label">Niveau d’engagement</span>
            <Select value={engagement} onChange={(e) => setEngagement(e.target.value as Engagement | '')}>
              <option value="">Non renseigné</option>
              {ENGAGEMENTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </Select>
          </label>
        </div>
        <div className="field-row">
          <Field
            label="Pool"
            value={pool}
            onChange={(e) => setPool(e.target.value)}
            placeholder="Bêta-testeurs, grands comptes…"
            list="ppl-pools"
            hint="Le groupe où vous la rangez."
          />
          <datalist id="ppl-pools">{pools.map((p) => <option key={p} value={p} />)}</datalist>
          <label className="zy-field">
            <span className="zy-field__label">Persona incarné</span>
            <Select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">Aucun</option>
              {(personas.data ?? []).map((p) => <option key={p.id} value={p.id}>{personaTitle(p)}</option>)}
            </Select>
          </label>
        </div>
        <div className="ppl-checks">
          <label className="ppl-check">
            <input className="zy-check" type="checkbox" checked={keyUser} onChange={(e) => setKeyUser(e.target.checked)} />
            Key user
          </label>
          <label className="ppl-check">
            <input className="zy-check" type="checkbox" checked={domainExpert} onChange={(e) => setDomainExpert(e.target.checked)} />
            Expert métier
          </label>
          <label className="ppl-check">
            <input className="zy-check" type="checkbox" checked={doNotContact} onChange={(e) => setDoNotContact(e.target.checked)} />
            Ne plus la solliciter
          </label>
        </div>
        <TextArea label="Notes" value={notes} onChange={setNotes} rows={3} placeholder="D’où vient le contact, ses disponibilités…" />
      </FormShell>
      {existing ? <ErasePerson projectId={projectId} person={existing} onDone={onErased ?? onDone} /> : null}
    </>
  );
}

/** Droit à l'effacement : la fiche ET ses invitations, sans retour possible. */
function ErasePerson({ projectId, person, onDone }: { projectId: string; person: Person; onDone: () => void }) {
  const erase = useErasePerson(projectId);
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="ppl-erase">
      {confirm ? (
        <>
          <p className="zy-texte-muet">
            Effacer {personName(person)} de l’annuaire
            {person.invitations > 0 ? `, avec ses ${person.invitations} invitation${person.invitations > 1 ? 's' : ''}` : ''} ?
            C’est définitif.
          </p>
          <div className="ppl-erase-actions">
            <Button
              variant="ghost"
              loading={erase.isPending}
              onClick={() => erase.mutate(person.id, { onSuccess: onDone })}
            >
              Effacer définitivement
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(false)}>Annuler</Button>
          </div>
        </>
      ) : (
        <Button variant="link-danger" size="s" type="button" onClick={() => setConfirm(true)}>
          Effacer cette personne
        </Button>
      )}
      {erase.error ? (
        <ErrorBanner>{erase.error instanceof ApiRequestError ? erase.error.message : 'Effacement impossible.'}</ErrorBanner>
      ) : null}
    </div>
  );
}
